// /webinar-calendar — standalone "add the webinar to your calendar" page.
//
// Purpose: a dedicated link we drop into reminder e-mails. The recipient lands
// here and does exactly one thing — add the event to their calendar. No form,
// no funnel, just the calendar buttons + a short line of context.
//
// Reuses the SAME <CalendarSection/> component as /webinar-thank-you and the
// SAME central date constant (src/lib/webinar.js), so the invite details never
// drift between pages — change the date once in webinar.js and this page,
// the thank-you page and the .ics all update together.

import React from 'react';
import { Icon } from './Icon.jsx';
import { LogoMark } from './LogoMark.jsx';
import { CookieBanner, AAConsent } from './CookieBanner.jsx';
import { CalendarSection } from './CalendarSection.jsx';
import { WEBINAR_DATE, WEBINAR_FORMAT, DATE_FMT, TIME_FMT } from '../lib/webinar.js';

// ─── HEAD ──────────────────────────────────────────────────────────────────
// Set the title + mark noindex — this is an e-mail landing page, not something
// we want in search results.
function useCalendarHead() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Webinar-Termin in deinen Kalender eintragen · AI App Academy';

    let robots = document.querySelector('meta[name="robots"]');
    const hadRobots = !!robots;
    const prevRobots = robots ? robots.getAttribute('content') : null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');

    return () => {
      document.title = prevTitle;
      if (hadRobots && prevRobots != null) robots.setAttribute('content', prevRobots);
      else if (!hadRobots && robots) robots.remove();
    };
  }, []);
}

function CalendarNav() {
  return (
    <nav className="nav nav-webinar">
      <div className="nav-inner">
        <a href="/" className="logo">
          <div className="logo-mark"><LogoMark size={32} /></div>
          <span className="logo-text">AI App Academy</span>
        </a>
      </div>
    </nav>
  );
}

// Compact intro — deliberately small so the calendar buttons sit right at the
// top of the page (this is an e-mail link; people should see the buttons
// immediately, not scroll past a full hero).
function CalendarIntro() {
  return (
    <section className="calendar-standalone-intro" id="top">
      <div className="hero-bg-grid" aria-hidden="true"></div>
      <div className="container">
        <div className="webinar-eyebrow">
          <span className="webinar-live-dot"></span>
          <span>Dein Webinar-Termin</span>
        </div>

        <h1 className="calendar-standalone-title">
          In deinen <em>Kalender</em> eintragen.
        </h1>

        <p className="calendar-standalone-sub">
          Ein Klick genügt. Titel, Datum und Uhrzeit sind schon vorausgefüllt.
        </p>

        <div className="event-meta-pill">
          <span className="event-meta-item">
            <Icon.Calendar size={16} />
            <span>{DATE_FMT.format(WEBINAR_DATE)}</span>
          </span>
          <span className="event-meta-divider"></span>
          <span className="event-meta-item">
            <Icon.Clock size={16} />
            <span>{TIME_FMT.format(WEBINAR_DATE)} Uhr</span>
          </span>
          <span className="event-meta-divider"></span>
          <span className="event-meta-item">
            <span>{WEBINAR_FORMAT}</span>
          </span>
        </div>
      </div>
    </section>
  );
}

function CalendarFooter() {
  return (
    <footer className="webinar-footer">
      <div className="container">
        <div className="webinar-footer-row">
          <div className="webinar-footer-brand">
            <div className="logo-mark"><LogoMark size={24} /></div>
            <span>© 2026 Incibit GmbH · App Academy</span>
          </div>
          <div className="webinar-footer-links">
            <a href="https://incibit.dev/impressum/" target="_blank" rel="noreferrer">Impressum</a>
            <a href="https://incibit.dev/privacy-policy/" target="_blank" rel="noreferrer">Datenschutz</a>
            <a href="https://incibit.dev/agb/" target="_blank" rel="noreferrer">AGB</a>
            <a
              href="#cookie-settings"
              onClick={(e) => { e.preventDefault(); AAConsent.openSettings(); }}
            >
              Cookie-Einstellungen
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
//  PAGE
// ═══════════════════════════════════════════════════════════════════════════

export default function WebinarCalendarPage() {
  useCalendarHead();
  return (
    <>
      <CalendarNav />
      <CalendarIntro />
      <CalendarSection source="email_calendar" showHead={false} className="calendar-standalone-grid" />
      <CalendarFooter />
      <CookieBanner />
    </>
  );
}
